import { CloseOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';

import { TableHeader, TimeColumn, TimetableCell } from './components';
import { TIME_SLOTS, WEEKDAY_LABELS } from './constants';
import { checkStartTimeIsBetween } from './helpers';
import { useDragDrop } from './hooks/useDragDrop';
import styles from './index.module.scss';
import type { ProgramBlock, ScheduleType } from './types';

type TimetableProps = {
  showInstructor?: boolean;
  scheduleData: ProgramBlock[];
  scheduleType: ScheduleType;
  columnHeaders?: string[];
  enableDrag?: boolean;
  columnField?: keyof ProgramBlock;
};

export const Timetable = ({
  showInstructor = false,
  scheduleData,
  scheduleType,
  columnHeaders = [],
  enableDrag = false,
  columnField = 'instructor',
}: TimetableProps) => {
  const [blocks, setBlocks] = useState<ProgramBlock[]>(scheduleData);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  const {
    draggedBlock,
    previewBlock,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragDrop({
    scheduleType,
    blocks,
    setBlocks,
    enableDrag,
    columnHeaders,
    columnField,
  });

  const selectedBlock = selectedBlockId
    ? blocks.find((block) => block.id === selectedBlockId)
    : null;

  const handleBlockClick = (
    event: React.MouseEvent<HTMLDivElement>,
    blockId: string
  ) => {
    event.stopPropagation();

    const wasSelected = selectedBlockId === blockId;
    setSelectedBlockId(wasSelected ? null : blockId);
  };

  const clearSelectedBlock = () => {
    setSelectedBlockId(null);
  };

  useEffect(() => {
    window.addEventListener('click', clearSelectedBlock);

    return () => {
      window.removeEventListener('click', clearSelectedBlock);
    };
  }, []);

  const getColumns = () => {
    if (scheduleType === 'columnSchedule') {
      const minColumns = 7;
      const actualColumnHeaders = columnHeaders.slice();

      while (actualColumnHeaders.length < minColumns) {
        actualColumnHeaders.push('');
      }

      return actualColumnHeaders;
    } else {
      return WEEKDAY_LABELS;
    }
  };

  const columns = getColumns();

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <TableHeader columns={columns} />

        <tbody>
          {TIME_SLOTS.map((time, tIdx) => (
            <tr key={time}>
              <TimeColumn time={time} />

              {columns.map((_, columnIdx) => {
                if (
                  scheduleType === 'columnSchedule' &&
                  !columnHeaders[columnIdx]
                ) {
                  return <td key={columnIdx} className={styles.blockTd}></td>;
                }

                const blocksInCell = blocks.filter((block) => {
                  if (scheduleType === 'columnSchedule') {
                    const columnValue = columnHeaders[columnIdx];
                    const isSameColumn = block[columnField] === columnValue;
                    const isBetween = checkStartTimeIsBetween(
                      time,
                      block.start
                    );
                    return isSameColumn && isBetween;
                  } else {
                    const isSameDay = block.columnIdx === columnIdx;
                    const isBetween = checkStartTimeIsBetween(
                      time,
                      block.start
                    );
                    return isSameDay && isBetween;
                  }
                });

                const allColumnBlocks = blocks.filter((block) => {
                  if (scheduleType === 'columnSchedule') {
                    const columnValue = columnHeaders[columnIdx];
                    return block[columnField] === columnValue;
                  } else {
                    return block.columnIdx === columnIdx;
                  }
                });

                return (
                  <TimetableCell
                    key={columnIdx}
                    position={{
                      columnIdx,
                      timeIdx: tIdx,
                    }}
                    data={{
                      blocks: blocksInCell,
                      allColumnBlocks,
                      previewBlock,
                      draggedBlock,
                      selectedBlockId,
                    }}
                    config={{
                      showInstructor,
                      scheduleType,
                      enableDrag,
                    }}
                    handlers={{
                      onBlockClick: handleBlockClick,
                      onDragStart: handleDragStart,
                      onDragOver: handleDragOver,
                      onDragLeave: handleDragLeave,
                      onDrop: handleDrop,
                    }}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {selectedBlock && (
        <div className={styles.tooltip}>
          <div className={styles.closeButtonContainer}>
            <button>
              <CloseOutlined rev={undefined} />
            </button>
          </div>
          <div className={styles.tooltipContent}>
            <h4>{selectedBlock.label}</h4>
            {selectedBlock.instructor && (
              <p>講師：{selectedBlock.instructor}</p>
            )}
            <p>
              時間：{selectedBlock.start} - {selectedBlock.end}
            </p>
            {selectedBlock.venue && (
              <p className={styles.venue}>
                地點：
                {selectedBlock.venue === '其他'
                  ? selectedBlock.address
                  : selectedBlock.venue}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
